'use client'

import React, { useState } from 'react'

import { Box } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import { tableContainerStyles, tableScrollAreaStyles } from './Table.styles'
import {
  TableColorSchemeContext,
  TableDensityContext,
  TableSortContext,
} from './TableContext'
import type { TableDensity } from './TableContext'
import { TablePagination } from './TablePagination'

export type { TableDensity } from './TableContext'

export type TableProps = {
  children: React.ReactNode
  className?: string
  colorScheme?: ColorScheme
  /** Controls row density. `comfortable` uses the 64px rebrand table row. */
  density?: TableDensity
  height?: string
  /** Caps the table width so content and footer stay aligned on ultrawide viewports. */
  maxWidth?: string
  /** Keys of all sortable columns. Each must match the `column` prop on a TableHeaderCell. */
  sortableColumns?: string[]
  /** Called whenever the active sort changes. `column` and `order` are null when sort returns to default. */
  onSortChange?: (column: string | null, order: 'asc' | 'desc' | null) => void
  /**
   * Controlled active sort column - when provided (with `activeOrder`), overrides
   * internal sort state instead of tracking it via clicks alone. Omit both to keep
   * the default uncontrolled behavior.
   */
  activeColumn?: string | null
  /** Controlled active sort order - pairs with `activeColumn`. */
  activeOrder?: 'asc' | 'desc' | null
  /**
   * When true, a third click on the active column cycles back to desc instead of
   * clearing to no-sort - a strict two-state toggle. For a consumer that swallows
   * the clear call instead, the active column would be a dead end: its only cycle
   * out of asc is that clear, which never fires, so it can never reach desc again.
   */
  disableSortClear?: boolean
}

export const Table = ({
  children,
  className,
  colorScheme = 'dark',
  density = 'default',
  height,
  maxWidth,
  sortableColumns = [],
  onSortChange,
  activeColumn: controlledActiveColumn,
  activeOrder: controlledActiveOrder,
  disableSortClear = false,
}: TableProps) => {
  const [internalActiveColumn, setInternalActiveColumn] = useState<
    string | null
  >(null)
  const [internalActiveOrder, setInternalActiveOrder] = useState<
    'asc' | 'desc' | null
  >(null)
  const isControlled = controlledActiveColumn !== undefined
  const activeColumn = isControlled
    ? controlledActiveColumn
    : internalActiveColumn
  const activeOrder = isControlled ? controlledActiveOrder : internalActiveOrder

  const setActiveState = (
    column: string | null,
    order: 'asc' | 'desc' | null
  ) => {
    if (!isControlled) {
      setInternalActiveColumn(column)
      setInternalActiveOrder(order)
    }
    onSortChange?.(column, order)
  }

  const handleColumnSort = (column: string) => {
    if (activeColumn !== column) {
      setActiveState(column, 'desc')
    } else if (activeOrder === 'desc') {
      setActiveState(column, 'asc')
    } else if (disableSortClear) {
      setActiveState(column, 'desc')
    } else {
      // asc → back to rest
      setActiveState(null, null)
    }
  }

  const childArray = React.Children.toArray(children)
  const scrollContent = childArray.filter(
    (child) => !(React.isValidElement(child) && child.type === TablePagination)
  )
  const pagination = childArray.filter(
    (child) => React.isValidElement(child) && child.type === TablePagination
  )

  return (
    <TableColorSchemeContext.Provider value={colorScheme}>
      <TableDensityContext.Provider value={density}>
        <TableSortContext.Provider
          value={{
            sortableColumns,
            activeColumn,
            activeOrder,
            onColumnSort: handleColumnSort,
          }}
        >
          <Box
            {...tableContainerStyles(colorScheme, height, maxWidth)}
            className={className}
          >
            <Box {...tableScrollAreaStyles(colorScheme)}>
              <Box
                display='flex'
                flexDirection='column'
                height='100%'
                minWidth='min-content'
                width='100%'
              >
                {scrollContent}
              </Box>
            </Box>
            {pagination}
          </Box>
        </TableSortContext.Provider>
      </TableDensityContext.Provider>
    </TableColorSchemeContext.Provider>
  )
}
